import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Modal from 'react-modal';
import { css } from 'emotion';
import Tracking from '@vromo/analytics';
import { Vehicle, Widget } from '@vromo/models';
import { Job } from '@vromo/models';
import { when } from 'mobx';
import { fadeIn, jackInTheBox } from '@vromo/ui/styles/animated';
import Color from 'color';
import { Theme } from '@emotion/react';

interface WidgetProps {
    widget: Widget;
    theme: Theme;
    vehicle: Vehicle;
    job: Job;
}

const Popup = forwardRef(function Popup({ widget, theme, job }: WidgetProps) {
    const [closed, setClosed] = useState(false);
    const [open, setOpen] = useState(false);

    function openPage() {
        Tracking.track('Promotion Widget Link Opened', {
            widget: widget.id
        });

        window.open(widget.metadata.link, '_blank');
    }

    useEffect(() => {
        return when(
            () => {
                switch (widget.settings.when) {
                    case 'dispatched':
                        return job.status === 'dispatched';
                    case 'underway':
                        return job.status === 'underway' && job.progress === 0;
                    case 'completed':
                        return job.status === 'completed';
                    case 'cancelled':
                        return job.status === 'cancelled';
                    case 'task0':
                        return job.progress === 1 && job.status === 'underway';
                    case 'task1':
                        return job.progress === 2 && job.status === 'underway';
                    case 'task2':
                        return job.progress === 3 && job.status === 'underway';
                    case 'task3':
                        return job.progress === 4 && job.status === 'underway';
                    default:
                        return false;
                }
            },
            () => {
                setOpen(true);
            }
        );
    }, []);

    return (
        <Modal
            isOpen={open && !closed}
            className={css`
                animation: ${jackInTheBox} 1s ease-in;
            `}
            style={{
                content: {
                    backgroundColor: 'white',
                    overflow: 'visible',
                    borderRadius: 34,
                    border: 'none',
                    padding: 0,
                    top: 'initial',
                    bottom: 'initial',
                    left: 'initial',
                    right: 'initial',
                    maxWidth: 450,
                    position: 'relative',
                    outline: 'none'
                },
                overlay: {
                    backgroundColor: 'rgba(0,0,0,0.32)',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 20
                }
            }}>
            <div
                className={css`
                    background-image: url(${widget.content});
                    background-size: cover;
                    background-position: center;
                    height: 225px;
                    width: 100%;
                    border-top-left-radius: 30px;
                    border-top-right-radius: 30px;

                    @media (max-height: 500px) {
                        height: 100px;
                    }
                `}
            />
            <div style={{ padding: '0 24px', textAlign: 'center' }}>
                <h2
                    className={css`
                        text-align: center;
                        font-size: 34px;
                        color: #3b3b3b;
                        margin: 20px;
                        @media (max-height: 500px) {
                            font-size: 24px;
                        }
                    `}>
                    {widget.metadata.title}
                </h2>
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    {widget.metadata.description}
                </div>
                {widget.metadata.callToAction ? (
                    <button
                        onClick={openPage}
                        className={css`
                            background-color: ${theme.accent};
                            border-radius: 1000px;
                            padding: 6px 50px;
                            font-size: 18px;
                            color: ${Color(theme.background).isLight() ? '#474747' : '#EFEFEF'};
                            border: none;
                            margin: 20px 0;
                            outline: none;
                            cursor: pointer;

                            @media (max-height: 500px) {
                                font-size: 16px;
                            }
                        `}>
                        {widget.metadata.callToAction}
                    </button>
                ) : null}
            </div>
            <div
                className={css`
                    opacity: 0;
                    animation: ${fadeIn} 1.2s ease-in;
                    animation-delay: 2s;
                    animation-fill-mode: both;
                `}
                onClick={() => setClosed(true)}
                style={{
                    display: 'flex',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 30,
                    height: 30,
                    zIndex: 1000,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                }}>
                <FontAwesome icon="times" weight="light" style={{ fontSize: 18 }} />
            </div>
        </Modal>
    );
});


const Inline = forwardRef(function Inline({ widget }: WidgetProps, ref: Ref<HTMLDivElement>) {
    return (
        <div
            ref={ref}
            className={css`
                margin-bottom: 8px;
                background-color: white;
                padding: 12px 12px;
            `}>
            <img src={widget.content} style={{ width: '100%' }} />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 4
                }}>
                <div>
                    <div style={{ fontSize: 18 }}>{widget.metadata.title}</div>
                    <div style={{ fontSize: 12, margin: '4px 0' }}>
                        {widget.metadata.description}
                    </div>
                </div>
                <a
                    href={widget.metadata.link}
                    onClick={(e) =>
                        Tracking.track('Promotion Widget Link Opened', {
                            widget: widget.id
                        })
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        flexShrink: 0,
                        backgroundColor: 'red',
                        padding: '8px 16px',
                        borderRadius: 4,
                        color: 'white',
                        textTransform: 'uppercase',
                        fontSize: 12,
                        border: '1px solid #DB0000',
                        textAlign: 'center'
                    }}>
                    {widget.metadata.callToAction}
                </a>
            </div>
        </div>
    );
});

export default observer<WidgetProps>(
    function Promotion({ widget, theme, vehicle, job }, ref: Ref<HTMLDivElement>) {

        const PromotionComponent = widget.settings.popup ?
            Popup :
            Inline;

        return (
            <PromotionComponent
                ref={ref}
                widget={widget}
                theme={theme}
                vehicle={vehicle}
                job={job}
            />
        );
    },
    {
        forwardRef: true
    }
);
